import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Search, SlidersHorizontal, Layers, Building2, MapPin, Hospital, PlusCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UpdateCard } from "@/components/UpdateCard";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const FILTERS = [
  { id: "category-filter-chip-all", label: "All", value: "All", icon: Layers },
  { id: "category-filter-chip-govt-health", label: "Govt & Health", value: "Govt & Health", icon: Building2 },
  { id: "category-filter-chip-city", label: "City", value: "City", icon: MapPin },
  { id: "category-filter-chip-hospitals", label: "Hospitals", value: "Hospitals", icon: Hospital },
  { id: "category-filter-chip-opps", label: "Opportunities", value: "Opportunities", icon: PlusCircle },
];

export default function Updates() {
  const { openPublish } = useOutletContext() || {};
  const { user } = useAuth();
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [updates, setUpdates] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (filter !== "All") params.category = filter;
    if (q) params.q = q;
    const { data } = await api.get("/updates", { params });
    setUpdates(data);
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="pb-6">
      {/* Hero */}
      <section data-testid="hero-banner-ahmedabad" className="relative mx-4 my-3 overflow-hidden rounded-2xl border border-slate-800 bg-navy p-5 text-white navy-grid">
        <div className="relative z-10 max-w-[80%]">
          <p className="mb-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-medorange">
            <MapPin className="h-3.5 w-3.5" /> Ahmedabad
          </p>
          <h1 className="font-heading text-2xl font-extrabold leading-tight sm:text-3xl">
            Your City.<br /><span className="text-medorange">Your Healthcare</span><br />Network.
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            Real-time hospital developments, regulatory directives, management & clinical opportunities.
          </p>
        </div>
        <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-medorange/20 blur-2xl" />
      </section>

      {/* Publish prompt */}
      <button
        data-testid="publish-update-trigger-card"
        onClick={() => (user && user.id ? openPublish && openPublish() : (window.location.href = "/login"))}
        className="mx-4 mb-3 flex w-[calc(100%-2rem)] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-medorange/50">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-medorange-light text-medorange"><PlusCircle className="h-5 w-5" /></span>
        <div>
          <p className="font-heading text-sm font-bold text-slate-900">Publish a News Update</p>
          <p className="text-xs text-slate-500">Share a hospital, regulatory or opportunity update</p>
        </div>
      </button>

      {/* Filters */}
      <div className="no-scrollbar flex items-center gap-2 overflow-x-auto px-4 py-2">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={f.id} onClick={() => setFilter(f.value)}
            className={cn("flex shrink-0 items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors",
              filter === f.value ? "bg-medorange text-white shadow-sm" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100")}>
            <f.icon className="h-3.5 w-3.5" /> {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mx-4 my-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
        <Search className="h-4 w-4 text-slate-400" />
        <input data-testid="updates-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by city or keyword…" className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400" />
        <SlidersHorizontal className="h-4 w-4 text-slate-400" />
      </div>

      {/* Feed */}
      <div className="mx-4 mb-2 mt-4 flex items-center gap-2">
        <span className="h-5 w-1 rounded-full bg-medorange" />
        <h2 className="font-heading text-lg font-bold text-slate-900">Latest Updates</h2>
      </div>
      <div className="space-y-4 px-4">
        {updates === null && [1, 2, 3].map((i) => <Skeleton key={i} className="h-48 w-full rounded-2xl" />)}
        {updates && updates.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No updates found.</p>}
        {updates && updates.map((u) => <UpdateCard key={u.id} update={u} onChange={load} />)}
      </div>
    </div>
  );
}
